import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LicensePanel } from "@/components/LicensePanel";
import { PortalConnectionCard } from "@/components/PortalConnectionCard";
import { UpdatesPanel } from "@/components/UpdatesPanel";
import { SelfHostedGraphSetup } from "@/components/SelfHostedGraphSetup";
import { ExoLeastPrivilege } from "@/components/ExoLeastPrivilege";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Cloud, Webhook, KeyRound, ShieldCheck, ShieldAlert, UserPlus, Lock, FolderSync, RefreshCw, Wifi, CalendarClock, Network, MessageSquare, Trash2, Users, Server, Copy, RotateCw, CheckCircle2, XCircle, AlertCircle, Mailbox, Mail, Code2, BellRing, Search, Loader2, Sparkles } from "lucide-react";
import { Section, sectionId } from "@/components/settings/Section";
import { ExoRoleGuide } from "@/components/ExoRoleGuide";
import { AgentSection } from "@/components/settings/AgentSection";
import { DangerZone } from "@/components/settings/DangerZone";
import { NamingSection } from "@/components/settings/NamingSection";
import { MessagesSection } from "@/components/settings/MessagesSection";
import { EmailNotifyPolicy } from "@/components/settings/EmailNotifyPolicy";
import { CloudSyncStatus } from "@/components/settings/CloudSyncStatus";
import { ReconcileSection } from "@/components/settings/ReconcileSection";
import { AiSection } from "@/components/settings/AiSection";
import { OuSettingsSection } from "@/components/settings/OuSettingsSection";

// Mutating actions route to the On-Prem Agent queue, Microsoft Graph, or Exchange Online.

const PREFIX_FIELDS = [
  ["licensing", "set.licensePrefix"],
];

const NOTIFY_EVENT_KEYS = [
  ["onboard", "set.evtOnboard"],
  ["offboard", "set.evtOffboard"],
  ["changes", "set.evtChanges"],
  ["errors", "set.evtErrors"],
];

function PwPolicyCompat({ compat, onFix }) {
  const { t } = useLang();
  const dir = compat.directory || {};
  const src = dir.source === "ad" ? t("pwc.sourceAd") : t("pwc.sourceEntra");
  const rep = (k, map) => { let s = t(k); Object.entries(map).forEach(([kk, vv]) => { s = s.replace(`{${kk}}`, vv); }); return s; };

  const details = [];
  if (typeof dir.minLength === "number") details.push(rep("pwc.detMin", { n: dir.minLength }));
  if (dir.complexityRequired === true) details.push(t("pwc.detComplexity"));
  else if (dir.complexityRequired === false) details.push(t("pwc.detComplexityNo"));
  if (typeof dir.passwordHistoryCount === "number") details.push(rep("pwc.detHistory", { n: dir.passwordHistoryCount }));
  if (typeof dir.maxPasswordAgeDays === "number" && dir.maxPasswordAgeDays > 0) details.push(rep("pwc.detMaxAge", { n: dir.maxPasswordAgeDays }));
  if (typeof dir.lockoutThreshold === "number" && dir.lockoutThreshold > 0) details.push(rep("pwc.detLockout", { n: dir.lockoutThreshold }));
  const pp = dir.passwordProtection || {};
  if (pp.configured && typeof pp.lockoutThreshold === "number") details.push(rep("pwc.protLockout", { n: pp.lockoutThreshold }));
  if (pp.configured && pp.customBannedWordCount > 0) details.push(rep("pwc.protBanned", { n: pp.customBannedWordCount }));

  const box = "mt-5 rounded-sm border px-3.5 py-3 text-sm";
  if (compat.status === "warning") {
    return (
      <div className={`${box} border-amber-200 bg-amber-50`} data-testid="pw-compat-warning">
        <div className="flex items-start gap-2.5">
          <ShieldAlert size={16} className="mt-0.5 shrink-0 text-amber-500" />
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-wider text-amber-700">{t("pwc.heading")}</div>
            <p className="mt-0.5 text-amber-800">{t("pwc.warnIntro").replace("{source}", src)}</p>
            <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-amber-800">
              {compat.issues.map((i, idx) => (
                <li key={idx} data-testid={`pw-compat-issue-${i.code}`}>
                  {i.code === "length" ? rep("pwc.warnLength", { current: i.current, min: i.min }) : rep("pwc.warnComplexity", { current: i.current })}
                </li>
              ))}
            </ul>
            {details.length > 0 && <p className="mt-1.5 text-xs text-amber-600">{src}: {details.join(" · ")}</p>}
            <Button size="sm" onClick={onFix} data-testid="pw-compat-fix" className="mt-3 rounded-sm bg-amber-600 hover:bg-amber-700">
              <RefreshCw size={13} className="mr-1.5" /> {t("pwc.fix")}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  if (compat.status === "unknown") {
    return (
      <div className={`${box} border-zinc-200 bg-zinc-50 text-zinc-500`} data-testid="pw-compat-unknown">
        <div className="flex items-start gap-2.5">
          <AlertCircle size={16} className="mt-0.5 shrink-0 text-zinc-400" />
          <div><div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("pwc.heading")}</div>
            <p className="mt-0.5">{t("pwc.unknown")}</p></div>
        </div>
      </div>
    );
  }
  return (
    <div className={`${box} border-emerald-200 bg-emerald-50`} data-testid="pw-compat-ok">
      <div className="flex items-start gap-2.5">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-500" />
        <div><div className="text-xs font-bold uppercase tracking-wider text-emerald-700">{t("pwc.heading")}</div>
          <p className="mt-0.5 text-emerald-800">{t("pwc.ok").replace("{source}", src)}</p>
          {dir.source === "entra" && <p className="mt-0.5 text-xs text-emerald-600">{t("pwc.entraFixed")}</p>}
          {details.length > 0 && <p className="mt-1.5 text-xs text-emerald-600">{src}: {details.join(" · ")}</p>}
        </div>
      </div>
    </div>
  );
}


function NotifyEventToggles({ channel, s, setS, t }) {
  const ev = ((s.notify || {})[channel] || {}).events || {};
  const setEvent = (k, v) => setS({
    ...s,
    notify: { ...(s.notify || {}), [channel]: { ...((s.notify || {})[channel] || {}), events: { ...ev, [k]: v } } },
  });
  return (
    <div className="mt-4 border-t border-zinc-100 pt-4" data-testid={`notify-events-${channel}`}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{t("set.evtTitle")}</div>
      <p className="mb-2 mt-0.5 text-[11px] text-zinc-400">{t("set.evtIntro")}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {NOTIFY_EVENT_KEYS.map(([key, label]) => (
          <label key={key} className="flex items-center justify-between gap-2 rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm" data-testid={`notify-event-${channel}-${key}`}>
            <span className="text-zinc-700">{t(label)}</span>
            <Switch checked={ev[key] !== false} onCheckedChange={(v) => setEvent(key, v)} data-testid={`notify-event-${channel}-${key}-toggle`} />
          </label>
        ))}
      </div>
    </div>
  );
}

function certExpiryInfo(iso) {
  if (!iso) return null;
  const now = new Date();
  const exp = new Date(iso);
  const days = Math.floor((exp - now) / 86400000);
  return { days, expired: days < 0, soon: days >= 0 && days <= 30, date: exp.toLocaleDateString() };
}

function SetupChecklist({ s, t, ssoStatus }) {
  const isSaaS = !!ssoStatus?.isSaaS;
  const certOk = !!(s.entra?.certUploaded || s.exo?.certUploaded);
  // In SaaS mode "Graph connected" reflects the one-click admin consent (graphConsented), not the
  // self-hosted app-registration fields; self-hosted keeps the tenant/client/secret check.
  const graphOk = isSaaS
    ? !!(ssoStatus?.graphConsented && !ssoStatus?.graphConsentStale)
    : !!(s.mode === "live" && s.entra?.tenantId && s.entra?.clientId && (certOk || s.entra?.clientSecretSet));
  const exoOk = !!s.exo?.org;
  const ssoOk = isSaaS ? !!ssoStatus?.enabled : !!s.sso?.enabled;
  const cert = certExpiryInfo(s.exo?.certExpiresAt);
  const steps = isSaaS
    ? [
        { ok: graphOk, label: t("set.chkGraph") },
        { ok: exoOk, label: t("set.chkExo") },
        { ok: ssoOk, label: t("set.chkSso"), optional: true },
      ]
    : [
        { ok: certOk, label: t("set.chkCert") },
        { ok: graphOk, label: t("set.chkGraph") },
        { ok: exoOk, label: t("set.chkExo") },
        { ok: ssoOk, label: t("set.chkSso"), optional: true },
      ];
  const done = steps.filter((x) => x.ok || x.optional === true && x.ok).length;
  const required = steps.filter((x) => !x.optional);
  const allReq = required.every((x) => x.ok);
  return (
    <Section icon={CheckCircle2} title={t("set.chkTitle")}>
      <p className="mb-3 text-xs text-zinc-500">{allReq ? t("set.chkAllDone") : t("set.chkIntro")}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {steps.map((x, i) => (
          <div key={i} data-testid={`setup-step-${i}`} className={`flex items-center gap-2 border px-3 py-2 text-sm ${x.ok ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200 bg-white"}`}>
            {x.ok ? <CheckCircle2 size={16} className="shrink-0 text-emerald-500" /> : <XCircle size={16} className={`shrink-0 ${x.optional ? "text-zinc-300" : "text-amber-500"}`} />}
            <span className={x.ok ? "text-zinc-800" : "text-zinc-500"}>{x.label}</span>
            {x.optional && <span className="ml-auto text-[10px] uppercase tracking-wider text-zinc-400">{t("set.permOptional")}</span>}
          </div>
        ))}
      </div>
      {cert && (cert.expired || cert.soon) && (
        <div data-testid="cert-expiry-warning" className={`mt-3 flex items-center gap-2 border px-3 py-2 text-xs font-medium ${cert.expired ? "border-red-200 bg-red-50 text-red-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
          <CalendarClock size={15} className="shrink-0" />
          {cert.expired ? t("set.certExpired").replace("{date}", cert.date) : t("set.certExpiringSoon").replace("{days}", cert.days).replace("{date}", cert.date)}
        </div>
      )}
      {cert && !cert.expired && !cert.soon && (
        <p className="mt-2 text-[11px] text-zinc-400" data-testid="cert-expiry-ok">{t("set.certValidUntil").replace("{date}", cert.date)}</p>
      )}
    </Section>
  );
}

function SaasStatusPill({ ok, stale, t }) {
  if (ok) return <span className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700"><CheckCircle2 size={13} /> {t("set.saasConnected")}</span>;
  if (stale) return <span className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-2 py-1 text-xs font-bold text-red-700"><XCircle size={13} /> {t("set.graphConsentStaleBadge")}</span>;
  return <span className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700"><XCircle size={13} /> {t("set.saasNotConnected")}</span>;
}

// SaaS: one consolidated "Microsoft Entra Connection" card. Everything is driven by a single
// one-click admin consent — no Tenant/App IDs, client secrets or certificate uploads to manage.
function SaasConnectionCard({ s, setS, ssoStatus, t, testing, connectGraphConsent, recheckGraphConsent, connectSso, detectOrg }) {
  const graphOk = !!(ssoStatus?.graphConsented && !ssoStatus?.graphConsentStale);
  const stale = !!ssoStatus?.graphConsentStale;
  const ssoOk = !!ssoStatus?.enabled;
  const exoOn = !!s.exo?.enabled;
  const [exoRoleReady, setExoRoleReady] = useState(null);
  // EXO is "connected" once the app holds the Exchange Administrator role. Reflect that in the badge.
  const exoReady = exoOn && (exoRoleReady === true || (!!s.exo?.org && exoRoleReady !== false));
  const [perms, setPerms] = useState(null);

  // Background auto-detect: poll the lightweight role-status while EXO is enabled but the role isn't
  // confirmed yet, so the badge turns green on its own once a freshly-assigned role propagates.
  useEffect(() => {
    if (!exoOn) { setExoRoleReady(null); return; }
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/settings/exo/role-status");
        if (!cancelled) setExoRoleReady(data?.roleReady === true);
        return data?.roleReady === true;
      } catch { return false; }
    };
    let id;
    check().then((ready) => { if (!ready && !cancelled) id = setInterval(async () => { if (await check()) clearInterval(id); }, 8000); });
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [exoOn]);

  useEffect(() => {
    if (!graphOk) { setPerms(null); return; }
    let cancelled = false;
    api.get("/wizard/graph-permissions").then((r) => { if (!cancelled) setPerms(r.data); }).catch(() => { if (!cancelled) setPerms(null); });
    return () => { cancelled = true; };
  }, [graphOk]);

  const [busy, setBusy] = useState(null);
  const [graphTest, setGraphTest] = useState(null);
  const [exoTest, setExoTest] = useState(null);
  const [exoGuideOpen, setExoGuideOpen] = useState(false);

  const runGraphSelftest = async () => {
    setBusy("graphtest"); setGraphTest(null);
    try {
      const { data } = await api.post("/auth/sso/graph-selftest");
      setGraphTest(data);
      if (data?.ok) toast.success(t("set.saasTestPass")); else toast.error(data?.message || t("set.saasTestFail"));
    } catch (e) { const m = apiErr(e); setGraphTest({ ok: false, message: m }); toast.error(m); }
    finally { setBusy(null); }
  };

  const runExoTest = async () => {
    setBusy("exotest"); setExoTest(null);
    try {
      const { data } = await api.post("/settings/exo/test");
      setExoTest({ ok: true, message: data?.message || t("set.saasExoRoleOk") });
      toast.success(data?.message || t("set.saasExoRoleOk"));
    } catch (e) { const m = apiErr(e); setExoTest({ ok: false, message: m }); toast.error(m); }
    finally { setBusy(null); }
  };

  const ResultPanel = ({ res, testid }) => res ? (
    <div data-testid={testid} className={`mt-3 flex items-start gap-2 rounded-md border p-3 text-xs ${res.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-red-200 bg-red-50 text-red-800"}`}>
      {res.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <XCircle size={15} className="mt-0.5 shrink-0" />}
      <div>
        <p className="font-bold">{res.ok ? t("set.saasTestPass") : t("set.saasTestFail")}</p>
        <p className="mt-0.5">{res.message}</p>
        {res.ok && res.grantedCount != null && (
          <p className="mt-0.5 font-medium opacity-80">{res.grantedCount}/{res.totalRequired} {t("set.saasGrantedPerms")}</p>
        )}
      </div>
    </div>
  ) : null;

  return (
    <Section icon={ShieldCheck} title={t("set.saasConnTitle")}>
      <p className="mb-4 text-sm text-zinc-500">{t("set.saasConnIntro")}</p>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={connectGraphConsent} data-testid="settings-graph-consent-btn" className="rounded-sm bg-primary hover:bg-[#002480]">
          <ShieldCheck size={15} className="mr-2" /> {graphOk ? t("set.saasRegrant") : t("set.saasGrantBtn")}
        </Button>
        <Button type="button" variant="outline" onClick={recheckGraphConsent} disabled={testing === "graphrecheck"} data-testid="settings-graph-recheck-btn" className="rounded-sm border-zinc-300">
          {testing === "graphrecheck" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RotateCw size={15} className="mr-2" />} {t("set.graphRecheckBtn")}
        </Button>
        <Button type="button" variant="outline" onClick={runGraphSelftest} disabled={busy === "graphtest"} data-testid="settings-graph-selftest-btn" className="rounded-sm border-zinc-300">
          {busy === "graphtest" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Wifi size={15} className="mr-2" />} {t("set.saasSelfTest")}
        </Button>
      </div>

      <ResultPanel res={graphTest} testid="saas-graph-selftest-result" />

      <div className="mt-4 divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200" data-testid="saas-connection-status">
        <div className="flex items-center gap-3 p-3" data-testid="saas-status-graph">
          <ShieldCheck size={18} className="shrink-0 text-zinc-600" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-zinc-900">{t("set.saasStatusGraph")}</div>
            <div className="text-xs text-zinc-500">{t("set.saasStatusGraphDesc")}</div>
          </div>
          <SaasStatusPill ok={graphOk} stale={stale} t={t} />
        </div>

        <div className="flex items-center gap-3 p-3" data-testid="saas-status-sso">
          <KeyRound size={18} className="shrink-0 text-zinc-600" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-zinc-900">{t("set.saasStatusSso")}</div>
            <div className="text-xs text-zinc-500">{t("set.saasStatusSsoDesc")}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Switch checked={!!s.sso?.enabled}
              onCheckedChange={(v) => setS({ ...s, sso: { ...(s.sso || {}), enabled: v },
                security: v ? s.security : { ...(s.security || {}), localLoginDisabled: false } })}
              data-testid="settings-sso-toggle" />
            {s.sso?.enabled && !ssoOk && (
              <Button type="button" variant="outline" size="sm" onClick={connectSso} data-testid="settings-sso-connect-btn" className="rounded-sm border-zinc-300">
                {t("set.saasVerifySso")}
              </Button>
            )}
            {s.sso?.enabled ? <SaasStatusPill ok={ssoOk} t={t} /> : <span className="text-xs font-medium text-zinc-400">{t("set.saasExoOff")}</span>}
          </div>
        </div>

        <div className="flex items-center gap-3 p-3" data-testid="saas-status-exo">
          <Mailbox size={18} className="shrink-0 text-zinc-600" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-zinc-900">{t("set.saasStatusExo")}</div>
            <div className="text-xs text-zinc-500">{t("set.saasStatusExoDesc")}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Switch checked={exoOn} onCheckedChange={(v) => setS({ ...s, exo: { ...(s.exo || {}), enabled: v } })} data-testid="settings-exo-toggle" />
            {exoOn && (
              <Button type="button" variant="outline" size="sm" onClick={runExoTest} disabled={busy === "exotest"} data-testid="settings-verify-exo-role-btn" className="rounded-sm border-zinc-300">
                {busy === "exotest" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <ShieldCheck size={14} className="mr-1.5" />} {t("set.saasVerifyExoRole")}
              </Button>
            )}
            {exoOn ? <SaasStatusPill ok={exoReady} t={t} /> : <span className="text-xs font-medium text-zinc-400">{t("set.saasExoOff")}</span>}
          </div>
        </div>
      </div>

      {exoOn && (
        <div className="mt-2 flex flex-wrap items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 p-3 text-[11px] text-sky-800" data-testid="saas-exo-hint">
          <ShieldCheck size={14} className="mt-0.5 shrink-0" /> <span className="min-w-0 flex-1">{t("set.saasExoHint")}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => setExoGuideOpen(true)} data-testid="settings-exo-guide-btn" className="shrink-0 rounded-sm border-sky-300 bg-white text-sky-800 hover:bg-sky-100">
            {t("exoGuide.trigger")}
          </Button>
        </div>
      )}

      {exoOn && <ResultPanel res={exoTest} testid="saas-exo-test-result" />}

      <ExoRoleGuide open={exoGuideOpen} onOpenChange={setExoGuideOpen} appId={ssoStatus?.graphAppId}
        onVerified={() => { setExoRoleReady(true); runExoTest(); }} />

      {ssoStatus?.graphCredential && (() => {
        const cr = ssoStatus.graphCredential;
        const d = cr.notAfter ? new Date(cr.notAfter) : null;
        const days = d ? Math.floor((d.getTime() - Date.now()) / 86400000) : null;
        const expired = days != null && days < 0;
        const soon = days != null && days >= 0 && days <= 30;
        const tone = expired ? "border-red-200 bg-red-50 text-red-700"
          : soon ? "border-amber-200 bg-amber-50 text-amber-800"
          : "border-zinc-200 bg-zinc-50 text-zinc-600";
        return (
          <div data-testid="saas-graph-credential" className={`mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-3 py-2 text-xs ${tone}`}>
            <KeyRound size={13} className="shrink-0" />
            <span className="font-bold">{t("set.saasCredType")}:</span>
            <span data-testid="saas-graph-credential-type" className="font-medium">{cr.source === "pem" ? t("set.saasCredPem") : t("set.saasCredPfx")}</span>
            <span className="opacity-40">·</span>
            <span data-testid="saas-graph-credential-expiry">
              {d ? `${expired ? t("set.saasCredExpired") : soon ? t("set.saasCredExpiringSoon") : t("set.saasCredValidUntil")} ${d.toLocaleDateString()}` : t("set.saasCredExpiryUnknown")}
            </span>
          </div>
        );
      })()}

      {graphOk && (
        <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50/60 p-4" data-testid="saas-org-identity">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.saasOrgIdentity")}</div>
            <Button type="button" variant="ghost" size="sm" onClick={detectOrg} disabled={testing === "detectorg"} data-testid="settings-detect-org-btn-saas" className="h-7 rounded-sm px-2 text-xs">
              {testing === "detectorg" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Search size={13} className="mr-1.5" />} {t("set.orgDetectBtn")}
            </Button>
          </div>
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div><dt className="text-[11px] uppercase tracking-wider text-zinc-400">{t("set.saasOrgName")}</dt><dd className="mt-0.5 font-medium text-zinc-800" data-testid="saas-org-company">{s.defaultCompany || "—"}</dd></div>
            <div><dt className="text-[11px] uppercase tracking-wider text-zinc-400">{t("set.tenantId")}</dt><dd className="mt-0.5 truncate font-mono text-xs text-zinc-700" data-testid="saas-org-tenant">{s.entra?.tenantId || "—"}</dd></div>
            <div><dt className="text-[11px] uppercase tracking-wider text-zinc-400">{t("set.saasOrgDomain")}</dt><dd className="mt-0.5 truncate font-mono text-xs text-zinc-700" data-testid="saas-org-domain">{s.defaultDomain || "—"}</dd></div>
          </dl>
        </div>
      )}

      {graphOk && perms && (perms.granted || []).length > 0 && (
        <div className="mt-3" data-testid="saas-granted-perms">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.saasGrantedPerms")}</div>
          <div className="flex flex-wrap gap-1.5">
            {perms.granted.map((p) => (
              <span key={p} data-testid={`saas-perm-chip-${p}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">
                <CheckCircle2 size={11} /> {p}
              </span>
            ))}
          </div>
          {(perms.missingRequired || []).length > 0 && (
            <p className="mt-2 text-xs font-medium text-amber-700" data-testid="saas-perm-missing">{t("set.permMissing")}</p>
          )}
        </div>
      )}

      {ssoStatus?.graphPermissionsRevoked && (
        <div className="mt-4 rounded-md border-2 border-red-300 bg-red-50 p-4" data-testid="graph-perms-revoked-alert">
          <div className="flex items-start gap-2.5">
            <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-600" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-800">{t("set.permRevokedTitle")}</p>
              <p className="mt-0.5 text-xs text-red-700">{t("set.permRevokedBody")}</p>
              {(ssoStatus?.missingRequiredPermissions || []).length > 0 && (
                <div className="mt-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-red-600">{t("set.permRevokedList")}</p>
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="graph-perms-revoked-list">
                    {ssoStatus.missingRequiredPermissions.map((p) => (
                      <span key={p} className="inline-flex items-center rounded-sm bg-red-100 px-2 py-0.5 font-mono text-[11px] font-medium text-red-800">{p}</span>
                    ))}
                  </div>
                </div>
              )}
              <Button type="button" onClick={connectGraphConsent} data-testid="graph-perms-reconsent-btn" className="mt-3 rounded-sm bg-red-600 hover:bg-red-700">
                <ShieldCheck size={15} className="mr-2" /> {t("set.permReconsent")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

export default function Settings() {
  const { t } = useLang();
  const { demoMode, license, ai } = useAuth();
  const aiLicensed = !!ai?.licensed;
  const showUpdates = !license?.saas;
  const licenseExpired = !!license?.expired;
  const communityLocked = license?.edition === "community" && !license?.managed;
  const [params, setParams] = useSearchParams();
  const TAB_VALUES = ["connection", "sync", "exchange", "agent", "onboarding", "lifecycle", "notifications", "messages", "naming", "security", ...(aiLicensed ? ["ai"] : []), ...(showUpdates ? ["updates"] : []), "license", "danger"];
  const activeTab = TAB_VALUES.includes(params.get("tab")) ? params.get("tab") : "connection";
  const setActiveTab = (v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true });

  // ---- Settings quick search (jump to any setting by name across tabs) ----
  const [q, setQ] = useState("");
  const [openResults, setOpenResults] = useState(false);
  const [activeResult, setActiveResult] = useState(0);
  const [tapPolicy, setTapPolicy] = useState(null);
  useEffect(() => { api.get("/settings/tap-policy").then((r) => setTapPolicy(r.data)).catch(() => {}); }, []);
  const [pwCompat, setPwCompat] = useState(null);
  const loadPwCompat = () => api.get("/settings/password-policy/compatibility").then((r) => setPwCompat(r.data)).catch(() => setPwCompat(null));
  useEffect(() => { loadPwCompat(); }, []);
  const applyPwSuggestion = async () => {
    if (!pwCompat?.suggestedPolicy) return;
    try {
      const { data } = await api.put("/settings", { passwordPolicy: pwCompat.suggestedPolicy });
      if (data?.passwordPolicy) setS((prev) => ({ ...prev, passwordPolicy: data.passwordPolicy }));
      const src = pwCompat.directory?.source === "ad" ? t("pwc.sourceAd") : t("pwc.sourceEntra");
      toast.success(t("pwc.fixed").replace("{source}", src));
      loadPwCompat();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const searchInputRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      const tag = (e.target.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      if (meta && (e.key === "k" || e.key === "K")) { e.preventDefault(); searchInputRef.current?.focus(); }
      else if (e.key === "/" && !typing) { e.preventDefault(); searchInputRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const TAB_LABEL = {
    connection: "set.tabConnection", sync: "set.tabSync",
    agent: "set.tabAgent", onboarding: "set.tabOnboarding", lifecycle: "set.tabLifecycle",
    notifications: "set.tabNotifications", messages: "set.tabMessages", naming: "set.tabNaming",
    schemas: "set.tabSchemas", security: "set.tabSecurity", license: "license.title", danger: "set.tabDanger",
  };
  const searchIndex = useMemo(() => {
    const S = (tab, key, section = true) => ({ tab, label: t(key), section });
    return [
      S("connection", "settings.graphSource"), S("connection", "set.opMode"), S("connection", "cloudsync.title"), S("connection", "settings.appReg"), S("connection", "set.exoTitle"), S("connection", "settings.sso"),
      S("onboarding", "settings.onboarding"), S("onboarding", "set.reqFieldsTitle"), S("onboarding", "set.cloudDescTitle"), S("onboarding", "settings.passwordPolicy"), S("onboarding", "set.pwDeliveryTitle"), S("onboarding", "set.tapTitle"),
      S("sync", "settings.directorySync"), S("sync", "set.tabSync", false),
      S("agent", "set.tabAgent", false), S("agent", "set.onpremSchemaTitle"),
      S("lifecycle", "settings.offboardJob"), S("lifecycle", "set.wfTimeoutTitle"), S("lifecycle", "set.licenseWatchTitle"), S("lifecycle", "set.smcTitle"),
      S("notifications", "set.teamsTitle"), S("notifications", "set.emailTitle"), S("notifications", "set.digestTitle"),
      S("messages", "set.tplPwTitle"), S("messages", "set.tplGuestTitle"),
      S("naming", "set.tabNaming", false),
      S("security", "set.secLocalTitle"), S("security", "set.secSsoRoleTitle"), S("security", "set.sessionTitle"),
      S("license", "license.title", false),
      S("danger", "set.tabDanger", false),
    ];
  }, [t]);
  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return searchIndex
      .filter((it) => it.label.toLowerCase().includes(query) || t(TAB_LABEL[it.tab]).toLowerCase().includes(query))
      .slice(0, 8);
  }, [q, searchIndex, t]);
  const jumpTo = (item) => {
    if (!item) return;
    setActiveTab(item.tab);
    setQ(""); setOpenResults(false); setActiveResult(0);
    setTimeout(() => {
      const el = item.section ? document.getElementById(sectionId(item.label)) : null;
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 1800);
      }
    }, 160);
  };
  const [s, setS] = useState(null);
  const [locations, setLocations] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(false);
  const [testing, setTesting] = useState("");
  const [cleanup, setCleanup] = useState({ users: false, groups: false, teams: false, licenses: false, workflows: false });
  const [agentStatus, setAgentStatus] = useState(null);
  const [agentToken, setAgentToken] = useState("");
  const [graphPerms, setGraphPerms] = useState(null);
  const [detecting, setDetecting] = useState(false);
  const [certDialog, setCertDialog] = useState({ open: false, file: null, pwd: "" });
  const [sharedMbx, setSharedMbx] = useState(null);
  const [secGroups, setSecGroups] = useState(null);
  const [scopeGroup, setScopeGroup] = useState("");
  const [rbacReady, setRbacReady] = useState(null);
  const checkReadiness = async () => {
    setTesting("rbac");
    try { const { data } = await api.get("/settings/exo/appaccess-readiness"); setRbacReady(data); }
    catch (e) { toast.error(apiErr(e)); setRbacReady(null); } finally { setTesting(""); }
  };
  const [schemas, setSchemas] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [roles, setRoles] = useState({ builtin: [], custom: [] });
  const [ssoStatus, setSsoStatus] = useState(null);
  const [teamsTestBusy, setTeamsTestBusy] = useState(false);
  const [lowSeatBusy, setLowSeatBusy] = useState(false);
  const [connMode, setConnMode] = useState(() => localStorage.getItem("connMode") || "assistant");
  useEffect(() => { localStorage.setItem("connMode", connMode); }, [connMode]);

  const loadAgent = () => api.get("/agent/status").then((r) => setAgentStatus(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
    api.get("/usage-locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/timezones").then((r) => setTimezones(r.data)).catch(() => {});
    api.get("/schemas").then((r) => setSchemas(r.data)).catch(() => {});
    api.get("/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/auth/sso/status").then((r) => setSsoStatus(r.data)).catch(() => {});
    loadAgent();
    const iv = setInterval(loadAgent, 15000);
    return () => clearInterval(iv);
  }, []);

  const isSaaS = !!ssoStatus?.isSaaS;
  const refreshSso = () => api.get("/auth/sso/status").then((r) => setSsoStatus(r.data)).catch(() => {});
  const connectSso = () => { window.location.href = `${API}/auth/sso/login`; };
  const connectGraphConsent = async () => {
    try {
      const { data } = await api.get("/auth/sso/graph-admin-consent");
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); }
  };
  const recheckGraphConsent = async () => {
    setTesting("graphrecheck");
    try {
      const { data } = await api.post("/auth/sso/reconcile-graph");
      await refreshSso();
      if (data.ok) window.dispatchEvent(new Event("cloud-sync-recheck"));
      toast[data.ok ? "success" : "error"](data.ok ? t("set.graphRecheckOk") : t("set.graphRecheckStale"));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setTesting(null); }
  };
  // Handle the consent/SSO return redirect (?graphConsent=ok / ?ssoError=...)
  useEffect(() => {
    const gc = params.get("graphConsent");
    const se = params.get("ssoError");
    if (gc === "ok") { toast.success(t("set.graphConsentOk")); refreshSso(); window.dispatchEvent(new Event("cloud-sync-recheck")); }
    if (se) { toast.error(se); }
    if (gc || se) {
      const u = new URL(window.location.href);
      u.searchParams.delete("graphConsent"); u.searchParams.delete("ssoError");
      window.history.replaceState({}, "", u.toString());
    }
  }, []);


  const rotateAgentToken = async () => {
    try { const { data } = await api.post("/agent/token/rotate"); setAgentToken(data.token); toast.success(t("agent.tokenGenerated")); loadAgent(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const revokeAgentToken = async () => {
    if (!window.confirm(t("agent.revokeConfirm"))) return;
    try { await api.delete("/agent/token"); setAgentToken(""); toast.success(t("agent.tokenRevoked")); loadAgent(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveAgentToken = async (token) => {
    try { await api.post("/agent/token/set", { token }); setAgentToken(""); toast.success(t("agent.tokenSaved")); loadAgent(); return true; }
    catch (e) { toast.error(apiErr(e)); return false; }
  };

  const detectCloudSync = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post("/settings/cloud-sync/detect");
      setS((p) => ({ ...p, cloudSync: { ...p.cloudSync, servicePrincipalId: data.servicePrincipalId,
        servicePrincipalName: data.servicePrincipalName, jobId: data.jobId, ruleId: data.ruleId } }));
      toast.success(data.message || t("cloudsync.detected"));
    } catch (e) { toast.error(apiErr(e)); } finally { setDetecting(false); }
  };

  const detectOrg = async () => {
    setTesting("detectorg");
    try {
      const { data } = await api.post("/settings/detect-organization");
      setS((p) => ({ ...p, defaultCompany: data.organization ?? p.defaultCompany,
        defaultDomain: data.defaultDomain ?? p.defaultDomain,
        entra: { ...p.entra, tenantId: data.tenantId ?? p.entra?.tenantId } }));
      toast.success(data.message || t("set.orgDetectOk"));
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  if (!s) return (<><PageHeader title={t("settings.title")} /><div className="p-10 font-mono text-xs uppercase tracking-widest text-zinc-400">{t("common.loading")}</div></>);

  const setEntra = (k, v) => setS({ ...s, entra: { ...s.entra, [k]: v } });
  const setPP = (k, v) => setS({ ...s, passwordPolicy: { ...s.passwordPolicy, [k]: v } });
  const setPfx = (k, v) => setS({ ...s, groupPrefixes: { ...s.groupPrefixes, [k]: v } });
  const setSync = (k, v) => setS({ ...s, groupSync: { ...s.groupSync, [k]: v } });

  const saveMode = async () => {
    setSavingMode(true);
    try {
      const { data } = await api.put("/settings", { operationMode: s.operationMode || "hybrid" });
      setS(data);
      toast.success(t("demo.modeSaved"));
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingMode(false); }
  };

  const save = async () => {
    if (demoMode) { toast.info(t("demo.settingsNoSave")); return; }
    setSaving(true);
    try {
      const body = {
        mode: s.mode,
        operationMode: s.operationMode || "hybrid",
        tap: { enabled: !!s.tap?.enabled, lifetimeInMinutes: Number(s.tap?.lifetimeInMinutes) || 60, isUsableOnce: s.tap?.isUsableOnce !== false },
        entra: { tenantId: s.entra.tenantId, clientId: s.entra.clientId, ...(s.entra.clientSecret && !s.entra.clientSecret.includes("•") ? { clientSecret: s.entra.clientSecret } : {}) },
        sso: { enabled: !!s.sso?.enabled },
        ...(s.webhookSecret && !s.webhookSecret.includes("•") ? { webhookSecret: s.webhookSecret } : {}),
        timezone: s.timezone || "UTC",
        autoDeleteAfterDays: Number(s.autoDeleteAfterDays) || 0,
        pollSeconds: Number(s.pollSeconds) || 300,
        verifyMaxAttempts: Number(s.verifyMaxAttempts) || 10,
        workflowActionTimeoutMinutes: Number(s.workflowActionTimeoutMinutes) || 30,
        notify: s.notify || { teams: { enabled: false, webhookUrl: "" }, email: { enabled: false, fromMailbox: "", toRecipients: "" } },
        defaultUsageLocation: s.defaultUsageLocation || "US",
        ssoRedirectUri: s.ssoRedirectUri || "",
        upnPattern: s.upnPattern || "",
        displayNamePattern: s.displayNamePattern || "",
        requiredFields: s.requiredFields || [],
        cloudUserDescription: { enabled: !!s.cloudUserDescription?.enabled },
        onPremExchangeSchema: s.onPremExchangeSchema !== false,
        passwordPolicy: s.passwordPolicy,
        groupPrefixes: s.groupPrefixes,
        groupSync: { enabled: !!s.groupSync?.enabled, intervalMinutes: Number(s.groupSync?.intervalMinutes) || 60 },
        userSync: { enabled: !!s.groupSync?.enabled, intervalMinutes: Number(s.groupSync?.intervalMinutes) || 60 },
        offboardJob: { enabled: !!s.offboardJob?.enabled, notifyWebhook: s.offboardJob?.notifyWebhook || "" },
        offboardDigestJob: { enabled: !!s.offboardDigestJob?.enabled, lookaheadDays: Number(s.offboardDigestJob?.lookaheadDays) || 7 },
        cloudSync: { enabled: !!s.cloudSync?.enabled, verifyMaxAttempts: Number(s.cloudSync?.verifyMaxAttempts) || 20, verifyBackoff: s.cloudSync?.verifyBackoff !== false },
        exo: { enabled: !!s.exo?.enabled, org: s.exo?.org || "" },
        namingConventions: s.namingConventions || [],
        templates: s.templates || {},
        emailTemplates: s.emailTemplates || {},
        defaultLanguage: s.defaultLanguage || "en",
        licenseWatch: { enabled: !!s.licenseWatch?.enabled, intervalSeconds: Number(s.licenseWatch?.intervalSeconds) || 300 },
        passwordDelivery: {
          toManager: !!s.passwordDelivery?.toManager, toCreator: !!s.passwordDelivery?.toCreator,
          toManual: !!s.passwordDelivery?.toManual, toFixed: !!s.passwordDelivery?.toFixed,
          fixedRecipient: s.passwordDelivery?.fixedRecipient || null,
          template: s.passwordDelivery?.template || "",
        },
        sharedMailboxConversion: {
          enabled: !!s.sharedMailboxConversion?.enabled, onOffboard: s.sharedMailboxConversion?.onOffboard !== false,
          onDelete: s.sharedMailboxConversion?.onDelete !== false, hideFromGAL: s.sharedMailboxConversion?.hideFromGAL !== false,
          keepDays: Number(s.sharedMailboxConversion?.keepDays) || 0,
          reminderDays: Number(s.sharedMailboxConversion?.reminderDays ?? 3) || 0,
          renamePattern: s.sharedMailboxConversion?.renamePattern || "", autoReply: s.sharedMailboxConversion?.autoReply || "",
        },
        onPremReconcile: {
          enabled: !!s.onPremReconcile?.enabled, includeMembership: s.onPremReconcile?.includeMembership !== false,
          notify: !!s.onPremReconcile?.notify,
          inventoryMaxAgeMinutes: Number(s.onPremReconcile?.inventoryMaxAgeMinutes ?? 180),
          digest: {
            enabled: !!s.onPremReconcile?.digest?.enabled,
            frequency: s.onPremReconcile?.digest?.frequency || "weekly",
            emails: s.onPremReconcile?.digest?.emails || "",
            teamsWebhook: s.onPremReconcile?.digest?.teamsWebhook || "",
          },
          autoRemediate: {
            enabled: !!s.onPremReconcile?.autoRemediate?.enabled,
            actions: {
              matchAd: !!s.onPremReconcile?.autoRemediate?.actions?.matchAd,
              reconcileMembership: !!s.onPremReconcile?.autoRemediate?.actions?.reconcileMembership,
              forceSync: !!s.onPremReconcile?.autoRemediate?.actions?.forceSync,
            },
          },
        },
        onPremOu: {
          defaultProvisioningOuDn: s.onPremOu?.defaultProvisioningOuDn || "",
          defaultGroupOuDn: s.onPremOu?.defaultGroupOuDn || "",
          deprovisioningOuDn: s.onPremOu?.deprovisioningOuDn || "",
          reconciliationOuDns: s.onPremOu?.reconciliationOuDns || [],
          autoDeltaSyncOnCreate: s.onPremOu?.autoDeltaSyncOnCreate !== false,
        },
        security: {
          localLoginDisabled: !!s.security?.localLoginDisabled,
          ssoDefaultRole: s.security?.ssoDefaultRole || "viewer",
        },
        sessionTimeoutHours: Number(s.sessionTimeoutHours) || 4,
      };
      const { data } = await api.put("/settings", body);
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const test = async (kind) => {
    setTesting(kind);
    try {
      const url = kind === "graph" ? "/settings/test-graph" : "/auth/sso/test";
      const { data } = await api.post(url);
      if (kind === "graph") setGraphPerms(data.permissions || []);
      toast.success(data.message || "Success");
    } catch (e) { if (kind === "graph") setGraphPerms(null); toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  const testExo = async () => {
    setTesting("exo");
    try {
      const { data } = await api.post("/settings/exo/test");
      toast.success(data.message || "Connected");
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  const uploadExoCert = async (file) => {
    if (!file) return;
    setCertDialog({ open: true, file, pwd: "" });
  };

  const confirmExoCert = async () => {
    const { file, pwd } = certDialog;
    if (!file) return;
    setCertDialog((p) => ({ ...p, open: false }));
    setTesting("exocert");
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const { data } = await api.post("/settings/exo/certificate", { pfx: b64, password: pwd || "" });
      setS((p) => ({ ...p, entra: { ...(p.entra || {}), certUploaded: true },
                     exo: { ...(p.exo || {}), certUploaded: true, ...(data.organization ? { org: data.organization } : {}) } }));
      toast.success(data.message || t("set.exoCertStored"));
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); setCertDialog({ open: false, file: null, pwd: "" }); }
  };

  const loadSharedMailboxes = async () => {
    setTesting("loadmbx");
    try { const { data } = await api.get("/settings/exo/shared-mailboxes"); setSharedMbx(data.mailboxes || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };
  const grantSend = async () => {
    const mbx = s.notify?.email?.fromMailbox;
    if (!mbx) { toast.error(t("set.emailSelectFrom")); return; }
    if (!scopeGroup) { toast.error(t("set.emailGroupSelect")); return; }
    setTesting("grantsend");
    try {
      const { data } = await api.post("/settings/exo/grant-send-permission", { mailbox: mbx, group: scopeGroup });
      toast.success(data.message);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.msg : apiErr(e), { duration: d?.rbacMissing ? 12000 : 5000 });
    } finally { setTesting(""); }
  };
  const loadSecurityGroups = async () => {
    setTesting("loadgrp");
    try { const { data } = await api.get("/settings/exo/security-groups"); setSecGroups(data.groups || []); }
    catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };
  const sendTestEmail = async () => {
    setTesting("testmail");
    try { const { data } = await api.post("/settings/exo/send-test-email", {}); toast.success(data.message); }
    catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  const syncNow = async () => {
    setTesting("sync");
    try {
      const { data } = await api.post("/sync/all");
      toast.success(`Synced ${data.users ?? 0} users · ${data.groups ?? 0} groups`);
      const r = await api.get("/settings"); setS(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  const runCleanup = async () => {
    if (!Object.values(cleanup).some(Boolean)) { toast.error(t("set.cleanupSelectOne")); return; }
    if (!window.confirm(t("set.cleanupConfirm"))) return;
    setTesting("cleanup");
    try {
      const { data } = await api.post("/admin/cleanup", cleanup);
      toast.success("Cleared: " + JSON.stringify(data.cleared));
      setCleanup({ users: false, groups: false, teams: false, licenses: false, workflows: false });
    } catch (e) { toast.error(apiErr(e)); } finally { setTesting(""); }
  };

  const setNotify = (k, v) => setS({ ...s, notify: { ...(s.notify || {}), [k]: v } });
  const setNotifyTeams = (k, v) => setS({ ...s, notify: { ...(s.notify || {}), teams: { ...((s.notify || {}).teams || {}), [k]: v } } });
  const setNotifyEmail = (k, v) => setS({ ...s, notify: { ...(s.notify || {}), email: { ...((s.notify || {}).email || {}), [k]: v } } });
  const setNotifySmtp = (k, v) => setS({ ...s, notify: { ...(s.notify || {}), email: { ...((s.notify || {}).email || {}), smtp: { ...(((s.notify || {}).email || {}).smtp || {}), [k]: v } } } });
  const setSmtpSecurity = (v) => {
    const defPort = { none: 25, starttls: 587, ssl: 465 }[v] || 587;
    const cur = ((s.notify || {}).email || {}).smtp || {};
    const knownDefaults = [25, 587, 465, "", null, undefined];
    const nextPort = knownDefaults.includes(cur.port) ? defPort : cur.port;
    setS({ ...s, notify: { ...(s.notify || {}), email: { ...((s.notify || {}).email || {}), smtp: { ...cur, security: v, port: nextPort } } } });
  };
  const setDigest = (k, v) => setS({ ...s, offboardDigestJob: { ...(s.offboardDigestJob || {}), [k]: v } });
  const setLowSeat = (k, v) => setNotify("lowSeat", { ...(s.notify?.lowSeat || {}), [k]: v });
  const sendLowSeatTest = async () => {
    setLowSeatBusy(true);
    try {
      const { data } = await api.post("/low-seats/test");
      const n = (data.low || []).length;
      toast.success(n > 0 ? t("set.lowSeatTestSent").replace("{n}", n) : t("set.lowSeatTestNone"));
    } catch (e) { toast.error(apiErr(e)); } finally { setLowSeatBusy(false); }
  };
  const sendTeamsTest = async () => {
    setTeamsTestBusy(true);
    try {
      const { data } = await api.post("/settings/notify/teams-test", { webhookUrl: s.notify?.teams?.webhookUrl || "" });
      toast.success(data.message);
    } catch (e) { toast.error(apiErr(e)); } finally { setTeamsTestBusy(false); }
  };
  const addEmailTo = (email) => {
    if (!email) return;
    const cur = (s.notify?.email?.toRecipients || "").split(",").map((x) => x.trim()).filter(Boolean);
    if (!cur.includes(email)) cur.push(email);
    setNotifyEmail("toRecipients", cur.join(", "));
  };

  const live = s.mode === "live";
  const envLocked = !!ssoStatus?.localLoginEnvLocked;
  const roleOptions = [...(roles.builtin || []), ...(roles.custom || [])].filter((r) => r.id !== "superadmin");

  return (
    <>
      <PageHeader title={t("settings.title")} subtitle={t("settings.subtitle")}>
        <Button onClick={save} disabled={saving} data-testid="save-settings" className="rounded-sm bg-primary hover:bg-[#002480]">
          <Save size={16} className="mr-2" /> {saving ? t("common.saving") : t("common.save")}
        </Button>
      </PageHeader>

      <div className="mx-auto w-full max-w-[1600px] p-6 md:p-10">
      {demoMode && (
        <div className="mb-6 flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="demo-settings-banner">
          <Lock size={15} className="mt-0.5 shrink-0" /> {t("demo.settingsExplore")}
        </div>
      )}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="relative mb-4 max-w-md" data-testid="settings-search">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            ref={searchInputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpenResults(true); setActiveResult(0); }}
            onFocus={() => setOpenResults(true)}
            onBlur={() => setTimeout(() => setOpenResults(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setQ(""); setOpenResults(false); return; }
              if (!results.length) return;
              if (e.key === "ArrowDown") { e.preventDefault(); setActiveResult((i) => (i + 1) % results.length); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setActiveResult((i) => (i - 1 + results.length) % results.length); }
              else if (e.key === "Enter") { e.preventDefault(); jumpTo(results[activeResult]); }
            }}
            placeholder={t("set.searchPlaceholder")}
            className="rounded-sm pl-9"
            data-testid="settings-search-input"
          />
          {openResults && results.length > 0 && (
            <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-sm border border-zinc-200 bg-white shadow-lg" data-testid="settings-search-results">
              {results.map((item, i) => (
                <button
                  key={`${item.tab}-${item.label}`}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); jumpTo(item); }}
                  onMouseEnter={() => setActiveResult(i)}
                  data-testid={`settings-search-result-${i}`}
                  className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm ${i === activeResult ? "bg-primary/5" : "bg-white"} hover:bg-primary/5`}
                >
                  <span className="truncate font-medium text-zinc-800">{item.label}</span>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-zinc-400">{t(TAB_LABEL[item.tab])}</span>
                </button>
              ))}
            </div>
          )}
          {openResults && q.trim() && results.length === 0 && (
            <div className="absolute z-30 mt-1 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-400 shadow-lg" data-testid="settings-search-empty">
              {t("set.searchNoResults")}
            </div>
          )}
        </div>
        <TabsList className="mb-6 flex h-auto flex-nowrap justify-start gap-1 overflow-x-auto rounded-sm bg-zinc-100 p-1 [&>button]:shrink-0 [&>button]:whitespace-nowrap">
          <TabsTrigger value="connection" data-testid="settings-tab-connection">{t("set.tabConnection")}</TabsTrigger>
          <TabsTrigger value="sync" data-testid="settings-tab-sync">{t("set.tabSync")}</TabsTrigger>
          <TabsTrigger value="agent" data-testid="settings-tab-agent">{t("set.tabAgent")}</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="settings-tab-onboarding">{t("set.tabOnboarding")}</TabsTrigger>
          <TabsTrigger value="lifecycle" data-testid="settings-tab-lifecycle">{t("set.tabLifecycle")}</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="settings-tab-notifications">{t("set.tabNotifications")}</TabsTrigger>
          <TabsTrigger value="messages" data-testid="settings-tab-messages">{t("set.tabMessages")}</TabsTrigger>
          <TabsTrigger value="naming" data-testid="settings-tab-naming">{t("set.tabNaming")}</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">{t("set.tabSecurity")}</TabsTrigger>
          {aiLicensed && <TabsTrigger value="ai" data-testid="settings-tab-ai">{t("set.tabAi")}</TabsTrigger>}
          {showUpdates && <TabsTrigger value="updates" data-testid="settings-tab-updates">{t("set.tabUpdates")}</TabsTrigger>}
          <TabsTrigger value="license" data-testid="settings-tab-license">{t("license.title")}</TabsTrigger>
          <TabsTrigger value="danger" data-testid="settings-tab-danger">{t("set.tabDanger")}</TabsTrigger>
        </TabsList>

        <TabsContent value="connection" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        {!isSaaS && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-white p-3" data-testid="conn-mode-switch">
            <div className="flex items-center gap-2 text-sm text-zinc-600">
              <Sparkles size={16} className="text-primary" />
              <span className="font-medium">{t("set.connModeLabel")}</span>
            </div>
            <div className="inline-flex rounded-sm border border-zinc-200 bg-zinc-100 p-0.5">
              <button type="button" onClick={() => setConnMode("assistant")} data-testid="conn-mode-assistant"
                className={`rounded-sm px-3 py-1 text-xs font-bold transition-colors ${connMode === "assistant" ? "bg-white text-primary shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                {t("set.connAssistant")}
              </button>
              <button type="button" onClick={() => setConnMode("manual")} data-testid="conn-mode-manual"
                className={`rounded-sm px-3 py-1 text-xs font-bold transition-colors ${connMode === "manual" ? "bg-white text-primary shadow-sm" : "text-zinc-500 hover:text-zinc-700"}`}>
                {t("set.connManual")}
              </button>
            </div>
          </div>
        )}
        {(isSaaS || connMode === "manual") && <SetupChecklist s={s} t={t} ssoStatus={ssoStatus} />}
        {(isSaaS || connMode === "manual") && (
        <Section icon={Cloud} title={t("settings.graphSource")}>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.entraConnect")}</div>
              <div className="text-sm text-zinc-500">{live ? t("set.entraConnectedDesc") : t("set.entraDisconnectedDesc")}</div>
            </div>
            <Switch checked={live} onCheckedChange={(v) => setS({ ...s, mode: v ? "live" : "mock" })} data-testid="settings-mode-toggle" />
          </label>
        </Section>
        )}

        <Section icon={Network} title={t("set.opMode")}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[["hybrid", t("set.opHybrid"), t("set.opHybridDesc")],
              ["cloudOnly", t("set.opCloudOnly"), t("set.opCloudOnlyDesc")]].map(([val, title, desc]) => {
              const locked = communityLocked && val === "hybrid";
              return (
              <button key={val} type="button" disabled={locked}
                onClick={() => !locked && setS({ ...s, operationMode: val })}
                data-testid={`opmode-${val}`} data-demo-allow
                className={`border p-4 text-left transition-colors ${locked ? "cursor-not-allowed border-zinc-200 bg-zinc-50 opacity-60" : (s.operationMode || "hybrid") === val ? "border-primary bg-primary/5" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-zinc-900">{title}</span>
                  {locked ? <Lock size={14} className="text-zinc-400" /> : (s.operationMode || "hybrid") === val && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                </div>
                <p className="mt-1 text-xs text-zinc-500">{locked ? t("set.opHybridLocked") : desc}</p>
              </button>
              );
            })}
          </div>
          {communityLocked && (
            <div className="mt-4 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="opmode-community-note">
              <Lock size={14} className="mt-0.5 shrink-0" /> <span>{t("set.opCommunityNote")}</span>
            </div>
          )}
          {demoMode && (
            <div className="mt-4 flex flex-wrap items-center gap-3 rounded-sm border border-sky-200 bg-sky-50 px-4 py-3" data-testid="demo-opmode-save-box">
              <p className="flex-1 text-xs text-sky-800">{t("demo.opModeHint")}</p>
              <Button onClick={saveMode} disabled={savingMode} data-testid="demo-save-opmode" className="rounded-sm bg-primary hover:bg-[#002480]">
                {savingMode && <Loader2 size={15} className="mr-2 animate-spin" />}{t("demo.saveMode")}
              </Button>
            </div>
          )}
        </Section>

        {(s.operationMode || "hybrid") === "hybrid" && (isSaaS || connMode === "manual") && (
        <Section icon={RefreshCw} title={t("cloudsync.title")}>
          <label className="flex items-start gap-3">
            <Switch checked={!!s.cloudSync?.enabled} onCheckedChange={(v) => setS({ ...s, cloudSync: { ...s.cloudSync, enabled: v } })} data-testid="settings-cloudsync-enabled" />
            <span>
              <span className="text-sm font-medium text-zinc-800">{t("cloudsync.enable")}</span>
              <span className="mt-1 block text-xs text-zinc-500">{t("cloudsync.desc")}</span>
            </span>
          </label>
          {s.cloudSync?.enabled && (
            <div className="mt-4 space-y-3">
              <Button variant="outline" onClick={detectCloudSync} disabled={detecting} data-testid="settings-cloudsync-detect" className="rounded-sm border-zinc-300">
                <RefreshCw size={14} className={`mr-2 ${detecting ? "animate-spin" : ""}`} /> {detecting ? t("cloudsync.detecting") : t("cloudsync.detect")}
              </Button>
              {s.cloudSync?.servicePrincipalId ? (
                <div className="space-y-1 border border-zinc-200 bg-zinc-50 p-3 font-mono text-xs text-zinc-600" data-testid="cloudsync-detected">
                  <div><span className="text-zinc-400">{t("cloudsync.sp")}:</span> {s.cloudSync.servicePrincipalName || s.cloudSync.servicePrincipalId}</div>
                  <div><span className="text-zinc-400">{t("cloudsync.job")}:</span> {s.cloudSync.jobId}</div>
                  <div><span className="text-zinc-400">{t("cloudsync.rule")}:</span> {s.cloudSync.ruleId}</div>
                </div>
              ) : (
                <p className="text-xs text-zinc-400">{t("cloudsync.notDetected")}</p>
              )}
            </div>
          )}
        </Section>
        )}

        {isSaaS ? (
          <>
          <SaasConnectionCard s={s} setS={setS} ssoStatus={ssoStatus} t={t} testing={testing}
            connectGraphConsent={connectGraphConsent} recheckGraphConsent={recheckGraphConsent}
            connectSso={connectSso} detectOrg={detectOrg} />
          <CloudSyncStatus s={s} t={t} />
          </>
        ) : connMode === "assistant" ? (
        <>
        <Section icon={ShieldCheck} title={t("set.guidedSetupTitle")}>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-sm border border-indigo-200 bg-indigo-50 p-3">
            <p className="min-w-0 flex-1 text-xs text-indigo-900">{t("set.assistantIntro")}</p>
            <Button type="button" onClick={() => window.dispatchEvent(new Event("open-setup-wizard"))} data-testid="connection-start-wizard" className="shrink-0 rounded-sm bg-indigo-600 hover:bg-indigo-700">
              <Sparkles size={15} className="mr-2" /> {t("set.startWizard")}
            </Button>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="assistant-status-chips">
            {(() => {
              const exoOn = !!(s.exo?.org || s.exo?.connected);
              const ssoOn = !!s.sso?.enabled;
              const chip = (on, onLabel, offLabel, Icon, testid) => (
                <span data-testid={testid} className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[11px] font-bold ${on ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-zinc-50 text-zinc-500"}`}>
                  <Icon size={12} /> {on ? onLabel : offLabel}
                </span>
              );
              return (
                <>
                  {chip(exoOn, t("set.chipExoOn"), t("set.chipExoOff"), Mailbox, "chip-exo")}
                  {chip(ssoOn, t("set.chipSsoOn"), t("set.chipSsoOff"), Lock, "chip-sso")}
                </>
              );
            })()}
          </div>
          <SelfHostedGraphSetup />
        </Section>
        <CloudSyncStatus s={s} t={t} />
        </>
        ) : (
        <>
        <Section icon={ShieldCheck} title={t("set.guidedSetupTitle")}>
          <SelfHostedGraphSetup />
        </Section>
        <Section icon={KeyRound} title={isSaaS ? t("set.graphDirTitle") : t("settings.appReg")}>
          {isSaaS ? (
            <div className="space-y-4" data-testid="graph-saas-consent">
              <p className="text-xs text-zinc-500">{t("set.graphSaasIntro")}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" onClick={connectGraphConsent} data-testid="settings-graph-consent-btn" className="rounded-sm bg-primary hover:bg-[#002480]">
                  <ShieldCheck size={15} className="mr-2" /> {t("set.graphConsentBtn")}
                </Button>
                <Button type="button" variant="outline" onClick={recheckGraphConsent} disabled={testing === "graphrecheck"} data-testid="settings-graph-recheck-btn" className="rounded-sm border-zinc-300">
                  {testing === "graphrecheck" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <RotateCw size={15} className="mr-2" />} {t("set.graphRecheckBtn")}
                </Button>
                {ssoStatus?.graphConsented && !ssoStatus?.graphConsentStale && (
                  <Button type="button" variant="outline" onClick={detectOrg} disabled={testing === "detectorg"} data-testid="settings-detect-org-btn-saas" className="rounded-sm border-zinc-300">
                    {testing === "detectorg" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Search size={15} className="mr-2" />} {t("set.orgDetectBtn")}
                  </Button>
                )}
                {ssoStatus?.graphConsented && !ssoStatus?.graphConsentStale
                  ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700" data-testid="graph-status-ok"><CheckCircle2 size={14} /> {t("set.graphAccessGranted")}</span>
                  : ssoStatus?.graphConsentStale
                  ? <span className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-2 py-1 text-xs font-bold text-red-700" data-testid="graph-status-stale"><XCircle size={14} /> {t("set.graphConsentStaleBadge")}</span>
                  : <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700" data-testid="graph-status-missing"><XCircle size={14} /> {t("set.graphNotConfigured")}</span>}
              </div>
              {ssoStatus?.graphPermissionsRevoked && (
                <div className="rounded-md border-2 border-red-300 bg-red-50 p-4" data-testid="graph-perms-revoked-alert">
                  <div className="flex items-start gap-2.5">
                    <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-600" />
                    <div className="flex-1">
                      <p className="text-sm font-bold text-red-800">{t("set.permRevokedTitle")}</p>
                      <p className="mt-0.5 text-xs text-red-700">{t("set.permRevokedBody")}</p>
                      {(ssoStatus?.missingRequiredPermissions || []).length > 0 && (
                        <div className="mt-2">
                          <p className="text-[11px] font-bold uppercase tracking-wider text-red-600">{t("set.permRevokedList")}</p>
                          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="graph-perms-revoked-list">
                            {ssoStatus.missingRequiredPermissions.map((p) => (
                              <span key={p} className="inline-flex items-center rounded-sm bg-red-100 px-2 py-0.5 font-mono text-[11px] font-medium text-red-800">{p}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      <Button type="button" onClick={connectGraphConsent} data-testid="graph-perms-reconsent-btn" className="mt-3 rounded-sm bg-red-600 hover:bg-red-700">
                        <ShieldCheck size={15} className="mr-2" /> {t("set.permReconsent")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
          <>
          <p className="mb-4 text-xs text-zinc-500">{t("set.appRegCertIntro")}</p>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tenantId")}</Label>
              <Input value={s.entra.tenantId || ""} onChange={(e) => setEntra("tenantId", e.target.value)} data-testid="settings-tenant" className="rounded-sm border-zinc-300 font-mono text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.clientId")}</Label>
              <Input value={s.entra.clientId || ""} onChange={(e) => setEntra("clientId", e.target.value)} data-testid="settings-client" className="rounded-sm border-zinc-300 font-mono text-sm" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.certificate")}</Label>
              <div className="flex items-center gap-2">
                <input type="file" accept=".pfx" id="graph-cert-file" className="hidden" onChange={(e) => uploadExoCert(e.target.files?.[0])} data-testid="settings-cert-file" />
                <Button type="button" variant="outline" disabled={testing === "exocert"} onClick={() => document.getElementById("graph-cert-file").click()} data-testid="settings-cert-btn" className="rounded-sm border-zinc-300">
                  <KeyRound size={15} className="mr-2" /> {testing === "exocert" ? t("set.testing") : t("set.certUpload")}
                </Button>
                {(s.entra?.certUploaded || s.exo?.certUploaded)
                  ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600" data-testid="cert-status-ok"><CheckCircle2 size={14} /> {t("set.certOk")}</span>
                  : <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600" data-testid="cert-status-missing"><XCircle size={14} /> {t("set.certMissing")}</span>}
              </div>
              <p className="text-[11px] text-zinc-400">{t("set.certHint")}</p>
            </div>
          </div>
          </>
          )}
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs text-zinc-400">{t("set.graphPerms")}</p>
            <div className="ml-3 flex shrink-0 items-center gap-2">
              <Button variant="outline" onClick={detectOrg} disabled={testing === "detectorg"} data-testid="settings-detect-org-btn" className="rounded-sm border-zinc-300">
                {testing === "detectorg" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Search size={15} className="mr-2" />} {t("set.orgDetectBtn")}
              </Button>
              <Button variant="outline" onClick={() => test("graph")} disabled={testing === "graph"} data-testid="test-graph-button" className="rounded-sm border-zinc-300">
                <Wifi size={15} className="mr-2" /> {testing === "graph" ? t("set.testing") : t("settings.testConn")}
              </Button>
            </div>
          </div>
          {graphPerms && (
            <div className="mt-4 border border-zinc-200 bg-zinc-50/60 p-4" data-testid="graph-permissions">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.permTitle")}</div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {graphPerms.map((p) => (
                  <div key={p.name} data-testid={`perm-${p.name}`} className="flex items-center gap-2 text-xs">
                    {p.granted
                      ? <CheckCircle2 size={15} className="shrink-0 text-emerald-500" data-testid={`perm-ok-${p.name}`} />
                      : <XCircle size={15} className={`shrink-0 ${p.required ? "text-red-500" : "text-amber-500"}`} data-testid={`perm-missing-${p.name}`} />}
                    <span className={`font-mono ${p.granted ? "text-zinc-700" : p.required ? "text-red-600" : "text-amber-600"}`}>{p.name}</span>
                    {!p.required && <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t("set.permOptional")}</span>}
                    {p.condition && <span className="truncate text-[10px] text-zinc-400" title={p.condition}>· {p.condition}</span>}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-zinc-400">{t("set.permHint")}</p>
            </div>
          )}
        </Section>

        <Section icon={Mailbox} title={t("set.exoTitle")}>
          <p className="mb-3 text-sm text-zinc-500">{t("set.exoDescCert")}</p>
          <div className="mb-4 flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 p-3 text-[11px] text-sky-800" data-testid="exo-cba-note">
            <ShieldCheck size={14} className="mt-0.5 shrink-0" />
            <span>{t("set.exoCbaNote")}</span>
          </div>
          {isSaaS && (
            <div className="mb-4 grid grid-cols-1 gap-5 border-b border-zinc-100 pb-4 md:grid-cols-2" data-testid="exo-saas-app">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.exoTenantId")}</Label>
                <Input value={s.entra.tenantId || ""} onChange={(e) => setEntra("tenantId", e.target.value)} data-testid="settings-exo-tenant" className="rounded-sm border-zinc-300 font-mono text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.exoAppId")}</Label>
                <Input value={s.entra.clientId || ""} onChange={(e) => setEntra("clientId", e.target.value)} data-testid="settings-exo-appid" className="rounded-sm border-zinc-300 font-mono text-sm" />
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.certificate")}</Label>
                <div className="flex items-center gap-2">
                  <input type="file" accept=".pfx" id="exo-cert-file" className="hidden" onChange={(e) => uploadExoCert(e.target.files?.[0])} data-testid="settings-exo-cert-file" />
                  <Button type="button" variant="outline" disabled={testing === "exocert"} onClick={() => document.getElementById("exo-cert-file").click()} data-testid="settings-exo-cert-btn" className="rounded-sm border-zinc-300">
                    <KeyRound size={15} className="mr-2" /> {testing === "exocert" ? t("set.testing") : t("set.certUpload")}
                  </Button>
                  {(s.entra?.certUploaded || s.exo?.certUploaded)
                    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600" data-testid="exo-cert-status-ok"><CheckCircle2 size={14} /> {t("set.certOk")}</span>
                    : <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600" data-testid="exo-cert-status-missing"><XCircle size={14} /> {t("set.certMissing")}</span>}
                </div>
                <p className="text-[11px] text-zinc-400">{t("set.certHint")}</p>
              </div>
            </div>
          )}
          <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <span className="text-sm font-medium text-zinc-800">{t("set.exoEnable")}</span>
            <Switch checked={!!s.exo?.enabled} onCheckedChange={(v) => setS({ ...s, exo: { ...(s.exo || {}), enabled: v } })} data-testid="settings-exo-toggle" />
          </label>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.exoOrg")}</Label>
            <div className="flex items-center gap-2">
              <Input value={s.exo?.org || ""} readOnly placeholder={t("set.exoOrgAuto")} data-testid="settings-exo-org" className="rounded-sm border-zinc-200 bg-zinc-50 font-mono text-sm text-zinc-600" />
              {s.exo?.org && <CheckCircle2 size={16} className="shrink-0 text-emerald-500" />}
            </div>
            <p className="text-[11px] text-zinc-400">{t("set.exoOrgAutoHint")}</p>
          </div>
          {s.exo?.enabled && s.exo?.org && (s.entra?.certUploaded || s.exo?.certUploaded) && (
            <div className="mt-4 border-t border-zinc-100 pt-4" data-testid="exo-leastpriv-settings">
              <ExoLeastPrivilege appId={s.entra?.clientId} />
            </div>
          )}
          <div className="mt-4 flex items-center justify-between border-t border-zinc-100 pt-4">
            <p className="text-xs text-zinc-400">{t("set.exoPermNote")}</p>
            <Button variant="outline" onClick={testExo} disabled={testing === "exo"} data-testid="test-exo-button" className="ml-3 shrink-0 rounded-sm border-zinc-300">
              <Wifi size={15} className="mr-2" /> {testing === "exo" ? t("set.testing") : t("set.exoTest")}
            </Button>
          </div>
        </Section>

        <Section icon={ShieldCheck} title={t("settings.sso")}>
          {isSaaS ? (
            <div className="space-y-4" data-testid="sso-saas-connect">
              <p className="text-xs text-zinc-500">{t("set.ssoSaasIntro")}</p>
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" onClick={connectSso} data-testid="settings-sso-connect-btn" className="rounded-sm bg-primary hover:bg-[#002480]">
                  <ShieldCheck size={15} className="mr-2" /> {t("set.ssoConnectBtn")}
                </Button>
                {ssoStatus?.enabled
                  ? <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700" data-testid="sso-status-ok"><CheckCircle2 size={14} /> {t("set.ssoActive")}</span>
                  : <span className="inline-flex items-center gap-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-700" data-testid="sso-status-missing"><XCircle size={14} /> {t("set.ssoNotConfigured")}</span>}
              </div>
            </div>
          ) : (
          <>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.enableMsSignIn")}</div>
              <div className="text-sm text-zinc-500">{t("set.ssoDesc")}</div>
            </div>
            <Switch checked={!!s.sso?.enabled} onCheckedChange={(v) => setS({ ...s, sso: { ...s.sso, enabled: v } })} data-testid="settings-sso-toggle" />
          </label>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.ssoRedirectCopy")}</Label>
            <div className="flex items-center gap-2">
              <Input value={s.ssoRedirectUriEffective || s.ssoRedirectUriDefault || ""} readOnly data-testid="settings-sso-redirect-effective" className="rounded-sm border-zinc-200 bg-zinc-50 font-mono text-xs text-zinc-700" />
              <Button type="button" variant="outline" data-testid="settings-sso-copy" onClick={() => { navigator.clipboard.writeText(s.ssoRedirectUriEffective || s.ssoRedirectUriDefault || ""); toast.success(t("set.ssoCopied")); }} className="shrink-0 rounded-sm border-zinc-300">
                <Copy size={15} />
              </Button>
            </div>
            <p className="text-[11px] text-zinc-400">{t("set.ssoRedirectCopyHint")}</p>
          </div>
          <div className="mt-4">
            <Button variant="outline" onClick={() => test("sso")} disabled={testing === "sso"} data-testid="test-sso-button" className="rounded-sm border-zinc-300">
              <ShieldCheck size={15} className="mr-2" /> {testing === "sso" ? t("set.testing") : t("set.testSso")}
            </Button>
          </div>
          </>
          )}
        </Section>
        </>
        )}
        </TabsContent>

        <TabsContent value="onboarding" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        <Section icon={UserPlus} title={t("settings.onboarding")}>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.defaultUsage")}</Label>
              <Select value={s.defaultUsageLocation || "US"} onValueChange={(v) => setS({ ...s, defaultUsageLocation: v })}>
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="settings-usage-location"><SelectValue /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.code} value={l.code}>{l.code} — {l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.upnPattern")}</Label>
              <Input value={s.upnPattern || ""} onChange={(e) => setS({ ...s, upnPattern: e.target.value })} data-testid="settings-upn-pattern" className="rounded-sm border-zinc-300 font-mono text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.displayNamePattern")}</Label>
              <Input value={s.displayNamePattern || ""} onChange={(e) => setS({ ...s, displayNamePattern: e.target.value })} placeholder="{firstname} {lastname}" data-testid="settings-displayname-pattern" className="rounded-sm border-zinc-300 font-mono text-sm" />
            </div>
          </div>
          <div className="mt-3 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-500">
            {t("set.upnTokens")}
          </div>
        </Section>

        <Section icon={ShieldCheck} title={t("set.reqFieldsTitle")}>
          <p className="mb-4 text-xs text-zinc-400">{t("set.reqFieldsDesc")}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[["companyName", "field.company"], ["department", "field.department"], ["jobTitle", "field.jobTitle"], ["manager", "field.manager"],
              ["mobilePhone", "field.mobile"], ["officeLocation", "field.officeNumber"],
              ["streetAddress", "field.street"], ["postalCode", "field.zip"], ["city", "field.city"]].map(([k, lbl]) => {
              const checked = (s.requiredFields || []).includes(k);
              return (
                <label key={k} className="flex items-center gap-2.5 text-sm text-zinc-700">
                  <Switch checked={checked} data-testid={`settings-reqfield-${k}`}
                    onCheckedChange={(v) => setS({ ...s, requiredFields: v
                      ? [...(s.requiredFields || []), k]
                      : (s.requiredFields || []).filter((x) => x !== k) })} />
                  {t(lbl)}
                </label>
              );
            })}
          </div>
        </Section>

        <Section icon={UserPlus} title={t("set.cloudDescTitle")}>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.cloudDescHeading")}</div>
              <div className="text-sm text-zinc-500">{t("set.cloudDescDesc")}</div>
            </div>
            <Switch checked={!!s.cloudUserDescription?.enabled} data-testid="settings-clouddesc-enabled"
              onCheckedChange={(v) => setS({ ...s, cloudUserDescription: { ...(s.cloudUserDescription || {}), enabled: v } })} />
          </label>
        </Section>

        <Section icon={Lock} title={t("settings.passwordPolicy")}>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.pwLength")}</Label>
              <Input type="number" min={8} max={64} value={s.passwordPolicy?.length || 16} onChange={(e) => setPP("length", Number(e.target.value))} data-testid="settings-pw-length" className="w-32 rounded-sm border-zinc-300" />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              {[["upper", t("set.pwUpper")], ["lower", t("set.pwLower")], ["digits", t("set.pwDigits")], ["symbols", t("set.pwSymbols")]].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2.5 text-sm text-zinc-700">
                  <Switch checked={!!s.passwordPolicy?.[k]} onCheckedChange={(v) => setPP(k, v)} data-testid={`settings-pw-${k}`} /> {lbl}
                </label>
              ))}
            </div>
          </div>
          {pwCompat && <PwPolicyCompat compat={pwCompat} onFix={applyPwSuggestion} />}
        </Section>

        <Section icon={Mail} title={t("set.pwDeliveryTitle")}>
          <p className="mb-4 text-xs text-zinc-400">{t("set.pwDeliveryIntro")}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[["toManager", "set.pwToManager"], ["toCreator", "set.pwToCreator"], ["toManual", "set.pwToManual"], ["toFixed", "set.pwToFixed"]].map(([k, lbl]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm text-zinc-700">
                <Switch checked={!!s.passwordDelivery?.[k]} onCheckedChange={(v) => setS({ ...s, passwordDelivery: { ...(s.passwordDelivery || {}), [k]: v } })} data-testid={`settings-pwd-${k}`} /> {t(lbl)}
              </label>
            ))}
          </div>
          {s.passwordDelivery?.toFixed && (
            <div className="mt-4 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.pwFixedRecipient")}</Label>
              <Select value={s.passwordDelivery?.fixedRecipient || ""} onValueChange={(v) => setS({ ...s, passwordDelivery: { ...(s.passwordDelivery || {}), fixedRecipient: v } })}>
                <SelectTrigger className="rounded-sm border-zinc-300 text-sm" data-testid="settings-pwd-fixed-recipient"><SelectValue placeholder={t("set.pwPickRecipient")} /></SelectTrigger>
                <SelectContent className="max-h-72">{recipients.map((r) => <SelectItem key={r.email} value={r.email}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.pwTemplate")}</Label>
            <p className="rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] leading-snug text-zinc-500" data-testid="settings-pwd-template-moved">{t("set.pwTemplateMoved")}</p>
          </div>
        </Section>
        <Section icon={KeyRound} title={t("set.tapTitle")}>
          {s.operationMode !== "cloudOnly" ? (
            <p className="text-sm text-amber-600" data-testid="tap-cloudonly-note">{t("set.tapCloudOnlyOnly")}</p>
          ) : (
            <div className="space-y-4">
              <label className="flex items-center justify-between gap-4">
                <span className="text-sm text-zinc-700">{t("set.tapEnable")}</span>
                <Switch checked={!!s.tap?.enabled} onCheckedChange={(v) => setS({ ...s, tap: { ...(s.tap || {}), enabled: v } })} data-testid="settings-tap-enabled" />
              </label>
              {s.tap?.enabled && (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.tapLifetime")}</Label>
                      <Input type="number" min={tapPolicy?.minimumLifetimeInMinutes || 10} max={tapPolicy?.maximumLifetimeInMinutes || 43200}
                        value={s.tap?.lifetimeInMinutes ?? 60}
                        onChange={(e) => setS({ ...s, tap: { ...(s.tap || {}), lifetimeInMinutes: Number(e.target.value) } })}
                        data-testid="settings-tap-lifetime" className="rounded-sm border-zinc-300 font-mono text-sm" />
                      {tapPolicy && <p className="text-[11px] text-zinc-400" data-testid="tap-bounds">{t("set.tapBounds").replace("{min}", tapPolicy.minimumLifetimeInMinutes ?? "—").replace("{max}", tapPolicy.maximumLifetimeInMinutes ?? "—")}</p>}
                    </div>
                    <label className="flex items-center justify-between gap-4 pt-6">
                      <span className="text-sm text-zinc-700">{t("set.tapOneTime")}</span>
                      <Switch checked={s.tap?.isUsableOnce !== false} onCheckedChange={(v) => setS({ ...s, tap: { ...(s.tap || {}), isUsableOnce: v } })} data-testid="settings-tap-oneuse" />
                    </label>
                  </div>
                  <p className="text-[11px] text-zinc-400">{t("set.tapHint")}</p>
                  {tapPolicy && tapPolicy.available && tapPolicy.isEnabled === false && (
                    <p className="text-[11px] text-amber-600" data-testid="tap-policy-disabled">{t("set.tapPolicyDisabled")}</p>
                  )}
                </>
              )}
            </div>
          )}
        </Section>
        </TabsContent>

        <TabsContent value="sync" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        <Section icon={FolderSync} title={t("settings.directorySync")}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {PREFIX_FIELDS.map(([k, lbl]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t(lbl)}</Label>
                <Input value={s.groupPrefixes?.[k] || ""} onChange={(e) => setPfx(k, e.target.value)} data-testid={`settings-prefix-${k}`} className="rounded-sm border-zinc-300 font-mono text-sm" />
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-zinc-400">{t("set.prefixHelp")}</p>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-4 border-t border-zinc-100 pt-4">
            <div className="flex items-end gap-5">
              <label className="flex items-center gap-2.5 text-sm text-zinc-700">
                <Switch checked={!!s.groupSync?.enabled} onCheckedChange={(v) => setSync("enabled", v)} data-testid="settings-sync-enabled" /> {t("set.recurringDirectorySync")}
              </label>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.everyMinutes")}</Label>
                <Input type="number" min={1} max={1440} value={s.groupSync?.intervalMinutes || 60} onChange={(e) => setSync("intervalMinutes", Number(e.target.value))} data-testid="settings-sync-interval" className="w-24 rounded-sm border-zinc-300" />
              </div>
            </div>
            <div className="text-right">
              <div className="mb-1 font-mono text-[11px] text-zinc-400">{t("set.lastDirectorySync")}: {(s.userSync?.lastSync || s.groupSync?.lastSync) ? new Date(s.userSync?.lastSync || s.groupSync?.lastSync).toLocaleString() : "—"}</div>
              <Button variant="outline" onClick={syncNow} disabled={testing === "sync"} data-testid="settings-sync-now" className="rounded-sm border-zinc-300">
                <RefreshCw size={15} className={`mr-2 ${testing === "sync" ? "animate-spin" : ""}`} /> {t("set.syncDirectoryNow")}
              </Button>
            </div>
          </div>
          <p className="mt-3 text-xs text-zinc-400">{t("set.directorySyncHelp")}</p>
        </Section>
        {s.operationMode === "cloudOnly" ? (
          <Section icon={RefreshCw} title={t("set.reconcileTitle") || "On-Prem ↔ Entra reconciliation"}>
            <p className="text-sm text-amber-600" data-testid="reconcile-cloudonly-note">{t("set.onpremNaCloudOnly")}</p>
          </Section>
        ) : (
          <ReconcileSection s={s} setS={setS} />
        )}
        </TabsContent>

        <TabsContent value="agent" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        {s.operationMode === "cloudOnly" ? (
          <Section icon={Server} title={t("set.tabAgent")}>
            <p className="text-sm text-amber-600" data-testid="agent-cloudonly-note">{t("set.onpremNaCloudOnly")}</p>
          </Section>
        ) : (
          <>
          <AgentSection s={s} setS={setS} agentStatus={agentStatus} agentToken={agentToken} rotateAgentToken={rotateAgentToken} revokeAgentToken={revokeAgentToken} saveAgentToken={saveAgentToken} onRefresh={loadAgent} />
          <OuSettingsSection s={s} setS={setS} />
          <Section icon={Server} title={t("set.onpremSchemaTitle")}>
            <label className="flex items-center justify-between">
              <div className="pr-6">
                <div className="text-sm font-bold text-zinc-900">{t("set.onpremSchemaHeading")}</div>
                <div className="text-sm text-zinc-500">{t("set.onpremSchemaDesc")}</div>
              </div>
              <Switch checked={s.onPremExchangeSchema !== false} data-testid="settings-onprem-exchange-schema"
                onCheckedChange={(v) => setS({ ...s, onPremExchangeSchema: v })} />
            </label>
          </Section>
          </>
        )}
        </TabsContent>

        <TabsContent value="lifecycle" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        <Section icon={CalendarClock} title={t("settings.offboardJob")}>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.offboardHeading")}</div>
              <div className="text-sm text-zinc-500">{t("set.offboardJobDesc")}</div>
            </div>
            <Switch checked={!!s.offboardJob?.enabled} onCheckedChange={(v) => setS({ ...s, offboardJob: { ...s.offboardJob, enabled: v } })} data-testid="settings-offboard-enabled" />
          </label>
          <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.timezone")}</Label>
              <Select value={s.timezone || "UTC"} onValueChange={(v) => setS({ ...s, timezone: v })}>
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="settings-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>{timezones.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.autoDelete")}</Label>
              <Input type="number" min={0} max={3650} value={s.autoDeleteAfterDays ?? 0} onChange={(e) => setS({ ...s, autoDeleteAfterDays: Number(e.target.value) })} data-testid="settings-auto-delete-days" className="w-40 rounded-sm border-zinc-300" />
            </div>
          </div>
        </Section>

        <Section icon={CalendarClock} title={t("set.wfTimeoutTitle")}>
          <div>
            <div className="text-sm font-bold text-zinc-900">{t("set.wfTimeoutHeading")}</div>
            <div className="text-sm text-zinc-500">{t("set.wfTimeoutDesc")}</div>
          </div>
          <div className="mt-4 space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.wfTimeoutLabel")}</Label>
            <Input type="number" min={1} max={1440} value={s.workflowActionTimeoutMinutes ?? 30}
              onChange={(e) => setS({ ...s, workflowActionTimeoutMinutes: Number(e.target.value) })}
              data-testid="settings-wf-timeout" className="w-32 rounded-sm border-zinc-300" />
          </div>
        </Section>

        <Section icon={KeyRound} title={t("set.licenseWatchTitle")}>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.licenseWatchHeading")}</div>
              <div className="text-sm text-zinc-500">{t("set.licenseWatchDesc")}</div>
            </div>
            <Switch checked={!!s.licenseWatch?.enabled} onCheckedChange={(v) => setS({ ...s, licenseWatch: { ...(s.licenseWatch || {}), enabled: v } })} data-testid="settings-licensewatch-enabled" />
          </label>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4 border-t border-zinc-100 pt-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.licenseWatchInterval")}</Label>
              <Input type="number" min={15} max={86400} value={s.licenseWatch?.intervalSeconds ?? 300} onChange={(e) => setS({ ...s, licenseWatch: { ...(s.licenseWatch || {}), intervalSeconds: Number(e.target.value) } })} data-testid="settings-licensewatch-interval" className="w-32 rounded-sm border-zinc-300" />
            </div>
            <div className="text-right font-mono text-[11px] text-zinc-400">
              {t("set.licenseWatchLastRun")}: {s.licenseWatch?.lastRun ? new Date(s.licenseWatch.lastRun).toLocaleString() : "—"}
            </div>
          </div>
        </Section>

        <Section icon={Mail} title={t("set.smcTitle")}>
          <label className="flex items-center justify-between">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.smcHeading")}</div>
              <div className="text-sm text-zinc-500">{t("set.smcDesc")}</div>
            </div>
            <Switch checked={!!s.sharedMailboxConversion?.enabled} onCheckedChange={(v) => setS({ ...s, sharedMailboxConversion: { ...(s.sharedMailboxConversion || {}), enabled: v } })} data-testid="settings-smc-enabled" />
          </label>
          {s.sharedMailboxConversion?.enabled && (
            <div className="mt-4 space-y-4 border-t border-zinc-100 pt-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {[["onOffboard", "set.smcOnOffboard"], ["onDelete", "set.smcOnDelete"], ["hideFromGAL", "set.smcHideGal"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2.5 text-sm text-zinc-700">
                    <Switch checked={s.sharedMailboxConversion?.[k] !== false} onCheckedChange={(v) => setS({ ...s, sharedMailboxConversion: { ...(s.sharedMailboxConversion || {}), [k]: v } })} data-testid={`settings-smc-${k}`} /> {t(lbl)}
                  </label>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smcKeepDays")}</Label>
                  <Input type="number" min={0} max={3650} value={s.sharedMailboxConversion?.keepDays ?? 0} onChange={(e) => setS({ ...s, sharedMailboxConversion: { ...(s.sharedMailboxConversion || {}), keepDays: Number(e.target.value) } })} data-testid="settings-smc-keepdays" className="w-32 rounded-sm border-zinc-300" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Erinnerung (Tage vorher)</Label>
                  <Input type="number" min={0} max={30} value={s.sharedMailboxConversion?.reminderDays ?? 3} onChange={(e) => setS({ ...s, sharedMailboxConversion: { ...(s.sharedMailboxConversion || {}), reminderDays: Number(e.target.value) } })} data-testid="settings-smc-reminderdays" className="w-32 rounded-sm border-zinc-300" />
                  <p className="text-[11px] text-zinc-400">0 = keine Erinnerung</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smcRename")}</Label>
                  <Input value={s.sharedMailboxConversion?.renamePattern || ""} onChange={(e) => setS({ ...s, sharedMailboxConversion: { ...(s.sharedMailboxConversion || {}), renamePattern: e.target.value } })} data-testid="settings-smc-rename" placeholder="{displayName} (Leaver)" className="rounded-sm border-zinc-300 font-mono text-sm" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smcAutoReply")}</Label>
                <p className="rounded-sm border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] leading-snug text-zinc-500" data-testid="settings-smc-autoreply-moved">{t("set.smcAutoReplyMoved")}</p>
              </div>
            </div>
          )}
        </Section>
        </TabsContent>

        <TabsContent value="notifications" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
        <Section icon={MessageSquare} title={t("set.teamsTitle")}>
          <p className="mb-4 text-xs text-zinc-400">{t("set.notifyIntro")}</p>
          <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-zinc-900"><MessageSquare size={15} /> {t("set.teamsWebhookTitle")}</div>
              <div className="text-sm text-zinc-500">{t("set.teamsWebhookDesc")}</div>
            </div>
            <Switch checked={!!s.notify?.teams?.enabled} onCheckedChange={(v) => setNotifyTeams("enabled", v)} data-testid="settings-teams-enabled" />
          </label>
          {s.notify?.teams?.enabled && (
            <div className="mt-4 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.teamsWebhookUrl")}</Label>
              <Input value={s.notify?.teams?.webhookUrl || ""} onChange={(e) => setNotifyTeams("webhookUrl", e.target.value)} placeholder="https://prod-XX.logic.azure.com/workflows/..." data-testid="settings-teams-webhook" className="rounded-sm border-zinc-300 font-mono text-xs" />
              <p className="text-[11px] text-zinc-400">{t("set.teamsWebhookHelp")}</p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Button type="button" variant="outline" onClick={sendTeamsTest} disabled={teamsTestBusy || !s.notify?.teams?.webhookUrl} data-testid="settings-teams-test" className="rounded-sm border-zinc-300">
                  {teamsTestBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <MessageSquare size={14} className="mr-2" />} {t("set.teamsTestBtn")}
                </Button>
                <span className="text-[11px] text-zinc-400">{t("set.teamsTestHint")}</span>
              </div>
              <NotifyEventToggles channel="teams" s={s} setS={setS} t={t} />
            </div>
          )}
        </Section>

        <Section icon={Mail} title={t("set.emailTitle")}>
          <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.emailEnable")}</div>
              <div className="text-sm text-zinc-500">{t("set.emailDesc")}</div>
            </div>
            <Switch checked={!!s.notify?.email?.enabled} onCheckedChange={(v) => setNotifyEmail("enabled", v)} data-testid="settings-email-enabled" />
          </label>
          {s.notify?.email?.enabled && (
            <>
              <div className="mt-4 space-y-1.5 max-w-md">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.emailTransport")}</Label>
                <Select value={s.notify?.email?.transport || "graph"} onValueChange={(v) => setNotifyEmail("transport", v)}>
                  <SelectTrigger className="rounded-sm border-zinc-300 text-sm" data-testid="settings-email-transport"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="graph" data-testid="settings-email-transport-graph">{t("set.emailTransportGraph")}</SelectItem>
                    <SelectItem value="smtp" data-testid="settings-email-transport-smtp">{t("set.emailTransportSmtp")}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-400">{t("set.emailTransportHint")}</p>
              </div>

              <div className="mt-4 space-y-1.5 max-w-md">
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.emailTo")}</Label>
                <Input value={s.notify?.email?.toRecipients || ""} onChange={(e) => setNotifyEmail("toRecipients", e.target.value)} placeholder="it-team@yourdomain.com" data-testid="settings-email-to" className="rounded-sm border-zinc-300 font-mono text-xs" />
                <Select value="" onValueChange={addEmailTo}>
                  <SelectTrigger className="rounded-sm border-zinc-300 text-xs" data-testid="settings-email-to-picker">
                    <SelectValue placeholder={t("set.emailToPickPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {(recipients || []).length === 0 && <div className="px-2 py-1.5 text-xs text-zinc-400" data-testid="settings-email-to-empty">{t("set.emailToPickEmpty")}</div>}
                    {(recipients || []).map((r) => <SelectItem key={r.email} value={r.email}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-400">{t("set.emailToHint")}</p>
              </div>

              <NotifyEventToggles channel="email" s={s} setS={setS} t={t} />

              {(s.notify?.email?.transport || "graph") === "graph" && (
                <EmailNotifyPolicy s={s} setNotifyEmail={setNotifyEmail} t={t} onGoConnection={() => setActiveTab("connection")} />
              )}

              {(s.notify?.email?.transport) === "smtp" && (
              <div className="mt-4 space-y-4 rounded-sm border border-zinc-200 bg-zinc-50 p-4" data-testid="smtp-config">
                <p className="text-xs text-zinc-500">{t("set.smtpIntro")}</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpHost")}</Label>
                    <Input value={s.notify?.email?.smtp?.host || ""} onChange={(e) => setNotifySmtp("host", e.target.value)} placeholder="smtp.yourdomain.com" data-testid="settings-smtp-host" className="rounded-sm border-zinc-300 font-mono text-xs" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpPort")}</Label>
                    <Input type="number" min={1} max={65535} value={s.notify?.email?.smtp?.port ?? 587} onChange={(e) => setNotifySmtp("port", Number(e.target.value) || 0)} data-testid="settings-smtp-port" className="rounded-sm border-zinc-300 font-mono text-xs" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpSecurity")}</Label>
                    <Select value={s.notify?.email?.smtp?.security || "starttls"} onValueChange={setSmtpSecurity}>
                      <SelectTrigger className="rounded-sm border-zinc-300 text-xs" data-testid="settings-smtp-security"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" data-testid="settings-smtp-security-none">{t("set.smtpSecNone")}</SelectItem>
                        <SelectItem value="starttls" data-testid="settings-smtp-security-starttls">{t("set.smtpSecStarttls")}</SelectItem>
                        <SelectItem value="ssl" data-testid="settings-smtp-security-ssl">{t("set.smtpSecSsl")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpUsername")}</Label>
                    <Input value={s.notify?.email?.smtp?.username || ""} onChange={(e) => setNotifySmtp("username", e.target.value)} placeholder="apikey / user@domain" data-testid="settings-smtp-username" className="rounded-sm border-zinc-300 font-mono text-xs" autoComplete="off" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpPassword")}</Label>
                    <Input type="password" value={s.notify?.email?.smtp?.password || ""} onChange={(e) => setNotifySmtp("password", e.target.value)} placeholder="••••••••" data-testid="settings-smtp-password" className="rounded-sm border-zinc-300 font-mono text-xs" autoComplete="new-password" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpFrom")}</Label>
                    <Input value={s.notify?.email?.smtp?.fromAddress || ""} onChange={(e) => setNotifySmtp("fromAddress", e.target.value)} placeholder="alerts@yourdomain.com" data-testid="settings-smtp-from" className="rounded-sm border-zinc-300 font-mono text-xs" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.smtpFromName")}</Label>
                    <Input value={s.notify?.email?.smtp?.fromName || ""} onChange={(e) => setNotifySmtp("fromName", e.target.value)} placeholder="Scalio Nexus" data-testid="settings-smtp-fromname" className="rounded-sm border-zinc-300 text-xs" />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" variant="outline" onClick={sendTestEmail} disabled={testing === "testmail" || !s.notify?.email?.smtp?.host || !s.notify?.email?.smtp?.fromAddress} data-testid="settings-smtp-test" className="rounded-sm border-zinc-300 bg-white">
                    {testing === "testmail" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />} {t("set.emailTestBtn")}
                  </Button>
                  <span className="text-[11px] text-zinc-400">{t("set.smtpTestHint")}</span>
                </div>
              </div>
              )}
            </>
          )}
        </Section>

        <Section icon={BellRing} title={t("set.digestTitle")}>
          <p className="mb-4 text-xs text-zinc-400">{t("set.digestIntro")}</p>
          <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-zinc-900"><BellRing size={15} /> {t("set.digestEnable")}</div>
              <div className="text-sm text-zinc-500">{t("set.digestDesc")}</div>
            </div>
            <Switch checked={!!s.offboardDigestJob?.enabled} onCheckedChange={(v) => setDigest("enabled", v)} data-testid="settings-digest-enabled" />
          </label>
          {s.offboardDigestJob?.enabled && (
            <div className="mt-4 max-w-xs space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.digestLookahead")}</Label>
              <Input type="number" min={1} max={90} value={s.offboardDigestJob?.lookaheadDays ?? 7}
                onChange={(e) => setDigest("lookaheadDays", e.target.value)}
                data-testid="settings-digest-lookahead" className="rounded-sm border-zinc-300" />
              <p className="text-[11px] text-zinc-400">{t("set.digestLookaheadHelp")}</p>
            </div>
          )}
        </Section>

        <Section icon={KeyRound} title={t("set.lowSeatTitle")}>
          <p className="mb-4 text-xs text-zinc-400">{t("set.lowSeatIntro")}</p>
          <label className="flex items-center justify-between border-b border-zinc-100 pb-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-zinc-900"><KeyRound size={15} /> {t("set.lowSeatEnable")}</div>
              <div className="text-sm text-zinc-500">{t("set.lowSeatDesc")}</div>
            </div>
            <Switch checked={!!s.notify?.lowSeat?.enabled} onCheckedChange={(v) => setLowSeat("enabled", v)} data-testid="settings-lowseat-enabled" />
          </label>
          <div className="mt-4 max-w-xs space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.lowSeatThreshold")}</Label>
            <Input type="number" min={0} max={100} value={s.notify?.lowSeat?.threshold ?? 2}
              onChange={(e) => setLowSeat("threshold", Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              data-testid="settings-lowseat-threshold" className="rounded-sm border-zinc-300" />
            <p className="text-[11px] text-zinc-400">{t("set.lowSeatThresholdHelp")}</p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" onClick={sendLowSeatTest} disabled={lowSeatBusy} data-testid="settings-lowseat-test" className="rounded-sm border-zinc-300 bg-white">
              {lowSeatBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BellRing size={14} className="mr-2" />} {t("set.lowSeatTestBtn")}
            </Button>
            <span className="text-[11px] text-zinc-400">{t("set.lowSeatTestHint")}</span>
          </div>
        </Section>
        </TabsContent>

        <TabsContent value="messages" className="space-y-8">
          <MessagesSection s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="naming" className="space-y-8">
          <NamingSection s={s} setS={setS} />
        </TabsContent>

        <TabsContent value="security" className="gap-6 xl:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
          <Section icon={Lock} title={t("set.secLocalTitle")}>
            <label className="flex items-center justify-between">
              <div className="pr-6">
                <div className="text-sm font-bold text-zinc-900">{t("set.secSsoOnlyToggle")}</div>
                <div className="text-sm text-zinc-500">{t("set.secSsoOnlyDesc")}</div>
              </div>
              <Switch checked={envLocked || !!s.security?.localLoginDisabled}
                disabled={envLocked || !s.sso?.enabled || (!ssoStatus?.ssoAdminExists && !s.security?.localLoginDisabled)}
                onCheckedChange={(v) => setS({ ...s, security: { ...(s.security || {}), localLoginDisabled: v } })}
                data-testid="settings-ssoonly-toggle" />
            </label>
            <div className="mt-3 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600" data-testid="settings-ssoonly-state">
              {(envLocked || s.security?.localLoginDisabled) ? t("set.secLocalOff") : t("set.secLocalOn")}
            </div>
            {!envLocked && !ssoStatus?.ssoAdminExists && !s.security?.localLoginDisabled && (
              <div className="mt-3 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="settings-ssoonly-needs-admin">
                <ShieldAlert size={14} className="mt-0.5 shrink-0" /> {t("set.secSsoOnlyNeedsAdmin")}
              </div>
            )}
            {envLocked && (
              <div className="mt-3 flex items-start gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800" data-testid="settings-ssoonly-envlocked">
                <Lock size={14} className="mt-0.5 shrink-0" /> {t("set.secEnvLocked")}
              </div>
            )}
          </Section>

          <Section icon={ShieldCheck} title={t("set.secSsoRoleTitle")}>
            <p className="mb-4 text-xs text-zinc-400">{t("set.secSsoRoleDesc")}</p>
            <div className="max-w-xs space-y-1.5">
              <Select value={s.security?.ssoDefaultRole || "viewer"}
                onValueChange={(v) => setS({ ...s, security: { ...(s.security || {}), ssoDefaultRole: v } })}>
                <SelectTrigger className="rounded-sm border-zinc-300" data-testid="settings-sso-default-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`sso-role-${r.id}`}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Section>

          <Section icon={CalendarClock} title={t("set.sessionTitle")}>
            <div>
              <div className="text-sm font-bold text-zinc-900">{t("set.sessionHeading")}</div>
              <div className="text-sm text-zinc-500">{t("set.sessionDesc")}</div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.sessionLabel")}</Label>
              <Input type="number" min={1} max={168} value={s.sessionTimeoutHours ?? 4}
                onChange={(e) => setS({ ...s, sessionTimeoutHours: Number(e.target.value) })}
                data-testid="settings-session-timeout" className="w-32 rounded-sm border-zinc-300" />
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="license" className="space-y-8">
          <LicensePanel />
          <PortalConnectionCard />
        </TabsContent>

        {showUpdates && (
          <TabsContent value="updates" className="space-y-8">
            <UpdatesPanel expired={licenseExpired} />
          </TabsContent>
        )}


        {aiLicensed && (
          <TabsContent value="ai" className="space-y-8">
            <AiSection s={s} onSaved={setS} />
          </TabsContent>
        )}

        <TabsContent value="danger" className="space-y-8">
          <DangerZone cleanup={cleanup} setCleanup={setCleanup} runCleanup={runCleanup} testing={testing} />
        </TabsContent>
      </Tabs>
      </div>

      <Dialog open={certDialog.open} onOpenChange={(o) => !o && setCertDialog({ open: false, file: null, pwd: "" })}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="exo-cert-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><KeyRound size={16} /> {t("set.exoCertUpload")}</DialogTitle>
            <DialogDescription className="text-xs">{certDialog.file?.name} — {t("set.exoCertPwdPrompt")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("set.exoCertPwdLabel")}</Label>
            <Input type="password" autoFocus value={certDialog.pwd}
              onChange={(e) => setCertDialog((p) => ({ ...p, pwd: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") confirmExoCert(); }}
              placeholder={t("set.exoCertPwdPlaceholder")} data-testid="exo-cert-pwd-input"
              className="rounded-sm border-zinc-300 font-mono text-sm" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCertDialog({ open: false, file: null, pwd: "" })} data-testid="exo-cert-cancel" className="rounded-sm border-zinc-300">{t("common.cancel")}</Button>
            <Button onClick={confirmExoCert} disabled={testing === "exocert"} data-testid="exo-cert-confirm" className="rounded-sm">{testing === "exocert" ? t("set.testing") : t("set.exoCertStore")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
